import { makeStyles } from '@mui/styles'
import React, { useEffect, useState, Fragment } from 'react'
import { StandardPage } from '../../components/_EOS/StandardPage/StandardPage'
import { ExternalAudtitSearch } from './ExternalAuditSearch'
import { Filter } from '../../components/_WMS/FilterComponent'
import { defaultFilters } from './config'
import { usePagination } from '../../hooks/usePagination'
import { getExternalAuditList } from './api'
import { ApiResponse } from '../../typings/ApiResponse'
import { useApi } from '../../hooks/useApi'
import { ExternalAudit } from './entities/ExternalAudit'
import { GenericTable } from '../../components/_EOS/Table/GenericTable'
import { generateTableHeadersExternalAudit } from '.'
import { Status } from '../../hooks/useFetchState'
import { GenericOrderBy } from '../../components/_EOS/Table/types'
import { useProductOrigins } from '../../hooks'
import { Box, Button } from '@mui/material'
import { useModal } from '../../hooks/useModal'
import { Modal } from '../../components/_EOS/Modal'
import { ExternalAuditDetailModal } from './ExternalAuditDetailModal'
import { SnackBar } from '../../components/SnackBar/Snackbar'
import { useSnackbar } from '../../hooks/useSnackbar'
import { eStatus } from '../../enums/eExternalAudit'

const useStyles = makeStyles({
  button: {
    borderRadius: '20px',
    padding: '4px 12px',
    fontSize: '11px',
    marginRight: '8px'
  },
  buttonPrimary: {
    backgroundColor: '#1976d2',
    color: 'white'
  },
  buttonOk: {
    backgroundColor: '#4caf50',
    color: 'white'
  },
  buttonError: {
    backgroundColor: '#f48fb1',
    color: 'black'
  }
})

interface ExternalAuditPageProps {}

export const ExternalAuditPage: React.FC<ExternalAuditPageProps> = () => {
  const [selectedFilters, setSelectedFilters] =
    React.useState<Filter[]>(defaultFilters)

  const classes = useStyles()
  const pagination = usePagination(10)
  const { isActive, isSuccess, message, openSnackBar } = useSnackbar()

  const [selectedElement, setSelectedElement] = React.useState<string[]>([])

  const [continuationTokensList, setContinuationTokens] = useState<
    Array<{ page: number; value: string }>
  >([{ page: 0, value: '' }])

  const handlePaginationChange = (page: number, pageSize: number) => {
    // Espera a que el estado se actualice antes de continuar
    setContinuationTokens((prevTokens) => {
      const tokens =
        pagination.pageSize !== pageSize ? [{ page: 0, value: '' }] : prevTokens

      return tokens
    })

    // If the pageSize is different, we should set page 0
    const auxPage = pagination.pageSize !== pageSize ? 0 : page
    setSelectedElement([])

    requestExternalAudit(selectedFilters, auxPage, pageSize)

    pagination.setPagination(auxPage, pageSize)
  }

  const { state, callApi } = useApi<ApiResponse<ExternalAudit>>()

  const [orderBy] = useState<GenericOrderBy<ExternalAudit>>({
    field: '',
    direction: 'desc'
  })

  const handleFilterStatus = (statusId: number) => {
    const newFilter = {
      key: 'externalStatusId',
      value: statusId,
      label: 'Estado',
      displayValue: eStatus[statusId]
    }

    setSelectedFilters([newFilter])

    requestExternalAudit([newFilter], pagination.page, pagination.pageSize)
  }

  const getTokenByPage = (page: number): string | undefined => {
    const tokenObject = continuationTokensList.find(
      (token) => token.page === page
    )
    return tokenObject ? tokenObject.value : undefined
  }

  const requestExternalAudit = (
    filters?: Filter[],
    page?: number,
    pageSize?: number
  ) => {
    const existingToken = getTokenByPage(page as number)

    callApi(getExternalAuditList(filters, page, pageSize, existingToken))
      .then((response) => {
        setContinuationTokens((prevTokens) => {
          const newPage: number = (page as number) + 1
          const tokenExists = prevTokens.some((token) => token.page === newPage)

          // guarda el continuation token, para el paginado
          if (!tokenExists && response.continuationToken) {
            const continuationToken = {
              page: newPage,
              value: response.continuationToken || ''
            }

            return [...prevTokens, continuationToken]
          }

          return prevTokens
        })
      })
      .catch((error) => {
        console.log(error)
      })
  }

  const tableHeadersExternalAudit = generateTableHeadersExternalAudit()

  useEffect(() => {
    if (selectedFilters.length === 0) {
      requestExternalAudit(
        selectedFilters,
        pagination.page,
        pagination.pageSize
      )
    }
  }, [orderBy, selectedFilters])

  const { getProductOrigins } = useProductOrigins()

  useEffect(() => {
    getProductOrigins()
  }, [])

  const externalAuditModal = useModal<{
    externaAuditId: string
  }>('')

  return (
    <Fragment>
      <StandardPage pageTitle='Administrador Logs Integración Externa'>
        <ExternalAudtitSearch
          selectedFilters={selectedFilters}
          setSelectedFilters={setSelectedFilters}
          onSearchClick={requestExternalAudit}
          includeRefreshSearch={true}
          includeAddButton={true}
        />
        <Box display={'flex'} justifyContent={'flex-end'}>
          <Button
            variant='contained'
            className={`${classes.button} ${classes.buttonPrimary}`}
            onClick={() => handleFilterStatus(eStatus.Ejecutado)}
          >
            Ejecutado
          </Button>
          <Button
            variant='contained'
            className={`${classes.button} ${classes.buttonOk}`}
            onClick={() => handleFilterStatus(eStatus.Satisfactorio)}
          >
            Satisfactorio
          </Button>
          <Button
            variant='contained'
            className={`${classes.button} ${classes.buttonError}`}
            onClick={() => handleFilterStatus(eStatus['Con Error'])}
          >
            Con Error
          </Button>
        </Box>

        <GenericTable<ExternalAudit>
          headersConfig={tableHeadersExternalAudit}
          IDKey={'id'}
          data={state.data?.items}
          pagination={{
            totalItem: state.data?.totalItems || 0,
            page: pagination.page,
            pageSize: pagination.pageSize
          }}
          onPaginationChange={handlePaginationChange}
          isLoading={state.status === Status.Loading}
          showCheckboxes={false}
          checkedElements={selectedElement}
          orderField={orderBy}
          actions={{
            onView: (_id) =>
              externalAuditModal.openModal(
                'md',
                { externaAuditId: _id as string },
                `Detalle Log Externo: ${_id}`
              )
          }}
        />
      </StandardPage>

      <Modal
        open={externalAuditModal.isOpen}
        maxWidth={externalAuditModal.size}
        title={externalAuditModal.title}
        onClose={externalAuditModal.closeModal}
      >
        <ExternalAuditDetailModal
          externalAuditId={
            externalAuditModal.extendedProps?.externaAuditId as string
          }
          handleSnackbar={openSnackBar}
          handleCloseModal={externalAuditModal.closeModal}
        />
      </Modal>

      <SnackBar
        message={message}
        open={isActive}
        status={isSuccess ? 'success' : 'error'}
      />
    </Fragment>
  )
}
